import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useListProductSources } from '@/shared/hooks/api/productSource'
import {
  MACHINE_LOCATIONS_KEY,
  useMachineLocationGenerationWorkflowLatest,
  useStartMachineLocationGeneration,
  type MachineLocationGenerationResult,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { WorkflowEventType } from '@/shared/hooks/api/workflows'
import { useQueryClient } from '@tanstack/react-query'

function getCreatedCount(result: MachineLocationGenerationResult | undefined) {
  return result?.createdCount ?? 0
}

const DEFAULT_MACHINE_LOCATION_GENERATION_COPY_PREFIX =
  'onboarding.machineLocations'

function applyGenerationCompletion(
  result: MachineLocationGenerationResult | undefined,
  translateCopy: (suffix: string) => string,
  setNoNewLocationsMessage: (value: boolean) => void
) {
  const createdCount = getCreatedCount(result)
  if (createdCount > 0) {
    toast.success(translateCopy('generateSuccess'))
    setNoNewLocationsMessage(false)
    return
  }
  setNoNewLocationsMessage(true)
}

export function useMachineLocationGeneration(params: {
  complianceId: string
  productId: string | undefined
  copyKeyPrefix?: string
}) {
  const {
    complianceId,
    productId,
    copyKeyPrefix = DEFAULT_MACHINE_LOCATION_GENERATION_COPY_PREFIX,
  } = params
  const { t } = useTranslation('machineryDirectiveCompliance')
  const translateCopy = useCallback(
    (suffix: string) => t(`${copyKeyPrefix}.${suffix}`),
    [copyKeyPrefix, t]
  )
  const queryClient = useQueryClient()
  const startGeneration = useStartMachineLocationGeneration(complianceId)
  const { data: latestWorkflow } = useMachineLocationGenerationWorkflowLatest(
    complianceId
  )
  const productSourcesQuery = useListProductSources(productId)

  const hasCompletedSource = useMemo(
    () =>
      productSourcesQuery.data?.some(
        (source) => source.ingestionStatus === 'COMPLETED'
      ) ?? false,
    [productSourcesQuery.data]
  )

  const generateDisabledDueToSources =
    productSourcesQuery.isSuccess && !hasCompletedSource

  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [noNewLocationsMessage, setNoNewLocationsMessage] = useState(false)
  const activeRunIdRef = useRef<string | null>(null)
  const handledRunIdRef = useRef<string | null>(null)
  const recoveryInitializedRef = useRef(false)
  const isUserRunRef = useRef(false)

  const isWorkflowRunning = latestWorkflow?.status === 'running'
  const isRunning = startGeneration.isPending || isWorkflowRunning

  useEffect(() => {
    if (recoveryInitializedRef.current || latestWorkflow == null) {
      return
    }
    recoveryInitializedRef.current = true
    if (
      latestWorkflow.status === 'running' &&
      latestWorkflow.runId != null
    ) {
      activeRunIdRef.current = latestWorkflow.runId
      setStatusMessage(translateCopy('generateProgressInitial'))
    }
  }, [latestWorkflow, translateCopy])

  useEffect(() => {
    if (!isRunning || latestWorkflow?.events == null) {
      return
    }
    const progressEvents = latestWorkflow.events.filter(
      (event) => event.type === WorkflowEventType.Progress
    )
    const lastProgress = progressEvents[progressEvents.length - 1]
    if (
      lastProgress != null &&
      'message' in lastProgress &&
      typeof lastProgress.message === 'string'
    ) {
      setStatusMessage(lastProgress.message)
    }
  }, [isRunning, latestWorkflow?.events])

  useEffect(() => {
    if (latestWorkflow == null) {
      return
    }

    const runId = latestWorkflow.runId
    if (
      runId == null ||
      activeRunIdRef.current !== runId ||
      isUserRunRef.current ||
      startGeneration.isPending
    ) {
      return
    }
    if (handledRunIdRef.current === runId) {
      return
    }

    if (latestWorkflow.status === 'completed') {
      handledRunIdRef.current = runId
      activeRunIdRef.current = null
      setStatusMessage(null)
      void queryClient.invalidateQueries({
        queryKey: [MACHINE_LOCATIONS_KEY, complianceId],
      })
      applyGenerationCompletion(
        parseRecoveryResult(latestWorkflow.result),
        translateCopy,
        setNoNewLocationsMessage
      )
    }

    if (latestWorkflow.status === 'failed') {
      handledRunIdRef.current = runId
      activeRunIdRef.current = null
      setStatusMessage(null)
      toast.error(translateCopy('generateFailed'))
    }
  }, [
    complianceId,
    latestWorkflow,
    queryClient,
    startGeneration.isPending,
    translateCopy,
  ])

  const handleGenerate = useCallback(() => {
    setNoNewLocationsMessage(false)
    setStatusMessage(translateCopy('generateProgressInitial'))
    isUserRunRef.current = true

    startGeneration.mutate(
      {
        onEvent: (event) => {
          if (
            event.type === WorkflowEventType.Progress &&
            'message' in event &&
            typeof event.message === 'string'
          ) {
            setStatusMessage(event.message)
          }
        },
        onComplete: (result) => {
          setStatusMessage(null)
          applyGenerationCompletion(result, translateCopy, setNoNewLocationsMessage)
        },
      },
      {
        onSettled: () => {
          isUserRunRef.current = false
        },
        onError: () => {
          setStatusMessage(null)
          toast.error(translateCopy('generateFailed'))
        },
      }
    )
  }, [startGeneration, translateCopy])

  useEffect(() => {
    if (startGeneration.isPending) {
      return
    }
    if (
      latestWorkflow?.status === 'running' &&
      latestWorkflow.runId != null &&
      activeRunIdRef.current == null
    ) {
      activeRunIdRef.current = latestWorkflow.runId
    }
  }, [latestWorkflow?.runId, latestWorkflow?.status, startGeneration.isPending])

  return {
    handleGenerate,
    isRunning,
    statusMessage,
    noNewLocationsMessage,
    generateDisabledDueToSources,
    hasCompletedSource,
    isSourcesLoading: productSourcesQuery.isLoading,
  }
}

function parseRecoveryResult(
  result: unknown
): MachineLocationGenerationResult | undefined {
  if (result == null || typeof result !== 'object') {
    return undefined
  }
  const record = result as Record<string, unknown>
  return {
    createdCount:
      typeof record.createdCount === 'number' ? record.createdCount : undefined,
  }
}
